// +----------------------------------------------------------------------
// | 我的决定（历史列表）
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listDecisions } from '../../api/decision';
import { categoryEmoji, categoryText, modeLabel, statusLabel } from '../../lib/categoryMap';
import { fromNow } from '../../lib/format';
import './DecisionHistory.less';

const PAGE_SIZE = 10;

const STATUS_FILTERS = [
  { value: '', label: '全部' },
  { value: 'pending', label: '进行中' },
  { value: 'decided', label: '已决定' },
  { value: 'undecided', label: '还没定' },
];

export default function DecisionHistory() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(
    async (nextPage, nextStatus, append) => {
      setLoading(true);
      setError('');
      try {
        const params = { page: nextPage, page_size: PAGE_SIZE };
        if (nextStatus) params.status = nextStatus;

        const data = await listDecisions(params);
        const items = (data && (data.list || data.items || data.data)) || [];
        const arr = Array.isArray(items) ? items : [];

        setList((prev) => (append ? [...prev, ...arr] : arr));
        setHasMore(arr.length >= PAGE_SIZE);
        setPage(nextPage);
      } catch (e) {
        setError(e.message || '列表没拉到');
        if (!append) setList([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(1, status, false);
  }, [load, status]);

  const changeStatus = (value) => {
    setStatus(value);
    setList([]);
  };

  return (
    <div className="page history">
      <h1 className="history-title">我的决定</h1>

      <div className="history-filters">
        {STATUS_FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`history-filter ${status === item.value ? 'is-active' : ''}`}
            onClick={() => changeStatus(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading && list.length === 0 && <div className="center muted">正在加载…</div>}

      {!loading && error && (
        <div className="card center">
          <p className="muted">{error}</p>
        </div>
      )}

      {!loading && !error && list.length === 0 && (
        <div className="card center history-empty">
          <p>这里还空着。</p>
          <p className="muted">下次纠结的时候，把它扔给我，就记在这里了。</p>
          <Link to="/" className="btn btn-primary btn-sm history-empty-btn">
            去想想
          </Link>
        </div>
      )}

      {list.length > 0 && (
        <ul className="history-list">
          {list.map((item) => (
            <li key={item.id}>
              <Link to={`/decision/${item.id}`} className="history-item">
                <span className="history-emoji">{categoryEmoji(item.category)}</span>

                <span className="history-main">
                  <span className="history-question">{item.title || item.question}</span>
                  <span className="history-meta muted">
                    {categoryText(item.category)}
                    {item.mode ? ` · ${modeLabel(item.mode)}` : ''}
                    {item.created_at ? ` · ${fromNow(item.created_at)}` : ''}
                  </span>
                </span>

                <span className="history-status">{statusLabel(item.status)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="center history-more">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => load(page + 1, status, true)}
            disabled={loading}
          >
            {loading ? '加载中…' : '再看一些'}
          </button>
        </div>
      )}
    </div>
  );
}
